import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { IoAddOutline } from 'react-icons/io5';
import { FaRegEdit } from "react-icons/fa";
import { MdOutlineDelete } from "react-icons/md";
import { BsCalendar4 } from "react-icons/bs";
import { toast } from 'react-toastify';
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import { useCustomDialog } from '../../context/DialogProvider';
import Stack from "@mui/material/Stack";
import Pagination from "@mui/material/Pagination";
import { format } from 'date-fns';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from '../../utils/api';
import { TbClockPlus } from "react-icons/tb";

const TIME_CONFIG = {
  FULL_DAY: { label: 'Full Day', duration: 1 },
  FIRST_HALF: { label: 'First Half', duration: 0.5 },
  SECOND_HALF: { label: 'Second Half', duration: 0.5 }
};

// ─── Status config ────────────────────────────────────────────────────────────
const statusConfig = {
  APPROVED: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", border: "border-emerald-200", label: "Approved" },
  PENDING:  { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-400",   border: "border-amber-200",   label: "Pending"  },
  REJECTED: { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500",     border: "border-red-200",     label: "Rejected" },
  CANCELLED:{ bg: "bg-slate-100",  text: "text-slate-500",   dot: "bg-slate-400",   border: "border-slate-200",   label: "Cancelled"},
};

const sessionLabel = (timing) => {
  if (timing === "FULL_DAY") return "Full Day";
  if (timing === "FIRST_HALF") return "First Half";
  return "Second Half";
};

const sessionColor = {
  FULL_DAY:    "bg-blue-100 text-blue-700",
  FIRST_HALF:  "bg-violet-100 text-violet-700",
  SECOND_HALF: "bg-indigo-100 text-indigo-700",
};

function E_CompOff() {
  const baseUrl = process.env.REACT_APP_API_URL;
  const { currentUser, loading } = useUser();
  const { confirm } = useCustomDialog();
  const [compOffs, setCompOffs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [newCompOff, setNewCompOff] = useState({
    onDate: '',
    duration: TIME_CONFIG.FULL_DAY.duration,
    reason: '',
    timing: 'FULL_DAY'
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewCompOff, setViewCompOff] = useState(null);
  const compOffsPerPage = 6;
  const [editingCompOffId, setEditingCompOffId] = useState(null);
  const [holidays, setHolidays] = useState([]);

  const totalPages = Math.ceil(compOffs.length / compOffsPerPage);
  const paginatedCompOffs = compOffs.slice(
    (currentPage - 1) * compOffsPerPage,
    currentPage * compOffsPerPage
  );

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = (`0${date.getMonth() + 1}`).slice(-2);
    const day = (`0${date.getDate()}`).slice(-2);
    return `${year}-${month}-${day}`;
  };
const isWeekdayAndNotHoliday = (date, holidays = []) => {
  if (!date || !Array.isArray(holidays)) return true;

  const day = date.getDay(); // 0=Sun, 6=Sat
  const dateOfMonth = date.getDate();

  const isSunday = day === 0;

  // Find which Saturday of the month this is
  const isSaturday = day === 6;
  const saturdayNumber = isSaturday
    ? Math.ceil(dateOfMonth / 7)
    : null;

  // 1st, 3rd and 5th Saturday should behave like working day
  const isWorkingSaturday =
    isSaturday &&
    [1, 3, 5].includes(saturdayNumber);

  const isWeekend = isSunday || (isSaturday && !isWorkingSaturday);

  const formatted = formatLocalDate(date);
  const isHoliday = holidays.some(
    holiday => holiday.holidayDate === formatted
  );

  return !isWeekend && !isHoliday;
};

const isWithinPreviousFiveDays = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDate = new Date(today);
  minDate.setDate(today.getDate() - 5);

  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  return checkDate >= minDate && checkDate <= today; // ✅ FIX
};

  const fetchHolidays = async () => {
    try {
      const response = await api.get(`/api/holidays`);
      setHolidays(response.data);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  useEffect(() => { fetchHolidays(); }, []);

  const fetchCompOffs = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/api/compoffs/${currentUser?.id}`);
      setCompOffs(response.data);
    } catch (error) {
      console.error('Error fetching comp offs:', error);
      toast.error('Failed to fetch comp off requests');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) fetchCompOffs();
  }, [currentUser?.id]);

  const handleDelete = async (id) => {
    const confirmed = await confirm("Are you sure you want to delete this comp off request?");
    try {
      if (confirmed) {
        await api.delete(`/api/compoffs/${id}`, { params: { employeeId: currentUser?.id } });
      }
      fetchCompOffs();
      toast.success('Comp off request deleted successfully');
    } catch (error) {
      toast.error('Failed to delete comp off request');
    }
  };

  const handleEdit = (compOff) => {
    setEditingCompOffId(compOff.id);
    setNewCompOff({ onDate: compOff.onDate, reason: compOff.reason, timing: compOff.timing });
    setIsEditing(true);
    setShowApplyForm(true);
  };

  const handleView = (compOff) => {
    setViewCompOff(compOff);
    setShowViewModal(true);
  };

  const formatDateForBackend = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const payload = {
    ...newCompOff,
    onDate: formatDateForBackend(newCompOff.onDate),
    employeeId: currentUser?.id
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isEditing) {
        await api.put(`/api/compoffs/${editingCompOffId}?employeeId=${currentUser?.id}`, payload);
        toast.success('Comp off request updated successfully');
      } else {
        await api.post(`/api/compoffs/apply?employeeId=${currentUser?.id}`, payload);
        toast.success('Comp off request submitted successfully');
      }
      fetchCompOffs();
      setShowApplyForm(false);
      setIsEditing(false);
      setNewCompOff({ onDate: '', reason: '', duration: TIME_CONFIG.FULL_DAY.duration, timing: 'FULL_DAY' });
    } catch (error) {
      toast.error(error.response.data);
      //toast.error(isEditing ? 'Failed to update comp off request' : `Failed to submit comp off request`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 am:mb-auto mb-20 w-full mx-auto">
      <motion.div
        className="flex justify-between items-center mb-6 bg-blue-100 rounded-xl p-4 sm:p-5 shadow-sm border border-slate-200"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className='flex items-center gap-4 text-xl xs:text-2xl font-bold truncate'>
          <div className='p-2 rounded-xl bg-slate-700 text-slate-100'><TbClockPlus className="text-2xl" /></div>
          <h2 className="text-xl xs:text-2xl font-bold">My Comp Offs</h2>
        </div>
        <motion.button
          onClick={() => setShowApplyForm(true)}
          className="bg-slate-800 text-white px-4 py-2 rounded-full hover:bg-slate-700 flex items-center gap-2"
        >
          <IoAddOutline className="text-lg" />
          <span>Add</span>
        </motion.button>
      </motion.div>

      {/* Desktop View — unchanged */}
      <motion.div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">SL No</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Comp Off Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Session</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Expires on</th>
                <th className="px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center"><div className="flex justify-center"><svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg></div></td></tr>
              ) : paginatedCompOffs.length > 0 ? (
                paginatedCompOffs.map((compOff, index) => (
                  <tr key={compOff.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                    <td className="px-6 py-4 text-slate-600">{(currentPage - 1) * compOffsPerPage + index + 1}</td>
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{
  format(
    new Date(
      new Date(compOff.onDate).toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
      })
    ),
    "dd-MM-yyyy"
  )
}</td>
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{sessionLabel(compOff.timing)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${compOff.comOffStatus === "APPROVED" ? "bg-green-100 text-green-800" : compOff.comOffStatus === "PENDING" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>
                        {compOff.comOffStatus === "APPROVED" ? "Approved" : compOff.comOffStatus === "PENDING" ? "Pending" : compOff.comOffStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{compOff.comOffStatus === "APPROVED" && compOff.expiresOn ? format(new Date(compOff.expiresOn), "dd-MM-yyyy") : "-"}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-3">
                        {compOff.comOffStatus === "PENDING" ? (
                          <>
                            <motion.button onClick={() => handleEdit(compOff)} className="text-yellow-600 hover:text-yellow-800" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} title="Edit"><FaRegEdit className="text-xl" /></motion.button>
                            <motion.button onClick={() => handleDelete(compOff.id)} className="text-red-600 hover:text-red-800" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} title="Delete"><MdOutlineDelete className="text-xl" /></motion.button>
                          </>
                        ) : (
                          <motion.button onClick={() => handleView(compOff)} className="text-blue-600 hover:text-blue-800" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} title="View">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          </motion.button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-400">No comp off requests found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination count={totalPages} page={currentPage} onChange={(_, page) => setCurrentPage(page)} variant="outlined" shape="rounded-full" size="small" showFirstButton showLastButton sx={{ "& .MuiPaginationItem-root": { color: "#475569", borderColor: "#e2e8f0" }, "& .Mui-selected": { backgroundColor: "#475569 !important", color: "#fff" } }} />
          </Stack>
        </div>
      </motion.div>

      {/* ─────────────────────────────────────────────────────────
          MOBILE VIEW — IMPROVED
      ───────────────────────────────────────────────────────── */}
      <div className="sm:hidden space-y-3">
        {isLoading ? (
          [1, 2, 3].map((item) => (
            <div key={item} className="bg-white p-4 rounded-2xl border border-slate-100 animate-pulse">
              <div className="flex justify-between items-start mb-3">
                <div className="space-y-2">
                  <div className="h-4 w-24 bg-slate-200 rounded-full"></div>
                  <div className="h-5 w-32 bg-slate-200 rounded"></div>
                </div>
                <div className="h-6 w-16 bg-slate-200 rounded-full"></div>
              </div>
              <div className="h-px bg-slate-100 my-3"></div>
              <div className="flex gap-8">
                <div className="h-3 w-20 bg-slate-200 rounded"></div>
                <div className="h-3 w-20 bg-slate-200 rounded"></div>
              </div>
            </div>
          ))
        ) : paginatedCompOffs.length > 0 ? (
          paginatedCompOffs.map((compOff, index) => {
            const status = statusConfig[compOff.comOffStatus] || statusConfig["PENDING"];
            const sColor = sessionColor[compOff.timing] || "bg-slate-100 text-slate-600";

            return (
              <motion.div
                key={compOff.id}
                onClick={() => compOff.comOffStatus !== "PENDING" && handleView(compOff)}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform cursor-pointer"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.04 }}
              >

                <div className="p-4">
                  {/* Row 1: session badge + status + actions */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      {/* Session avatar */}
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[10px] font-bold tracking-wide shrink-0 ${sColor}`}>
                        {compOff.timing === "FULL_DAY" ? "FD" : compOff.timing === "FIRST_HALF" ? "FH" : "SH"}
                      </div>
                      <div>
                        <p className="text-[13px] font-semibold text-slate-800 leading-tight">Comp Off</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">#{(currentPage - 1) * compOffsPerPage + index + 1}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* Status pill */}
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${status.bg} ${status.text} ${status.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                        {status.label}
                      </span>

                      {/* Action buttons */}
                      {compOff.comOffStatus === "PENDING" && (
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <motion.button
                            onClick={() => handleEdit(compOff)}
                            className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                            whileTap={{ scale: 0.9 }}
                          >
                            <FaRegEdit className="text-sm" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDelete(compOff.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                            whileTap={{ scale: 0.9 }}
                          >
                            <MdOutlineDelete className="text-sm" />
                          </motion.button>
                        </div>
                      )}
                      
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-100 my-3" />

                  {/* Row 2: date + session label */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
                      <BsCalendar4 className="text-slate-400 shrink-0" />
                      <span className="font-medium text-slate-700">{format(new Date(compOff.onDate), "dd-MM-yyyy")}</span>
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${sColor}`}>
                      {sessionLabel(compOff.timing)}
                    </span>
                  </div>

                  {/* Row 3: reason */}
                  {compOff.reason && (
                    <p className="mt-2.5 text-[12px] text-slate-400 leading-relaxed line-clamp-1">
                      <span className="font-medium text-slate-500">Reason: </span>{compOff.reason}
                    </p>
                  )}

                  {/* Row 4: expires on (if approved) */}
                  {compOff.comOffStatus === "APPROVED" && compOff.expiresOn && (
                    <p className="mt-1.5 text-[11px] text-slate-400">
                      <span className="font-medium text-slate-500">Expires: </span>
                      {format(new Date(compOff.expiresOn), "dd-MM-yyyy")}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
            <TbClockPlus className="text-4xl text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-400">No comp off requests found</p>
          </div>
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-[12px] text-slate-400 font-medium">
              Page <span className="text-slate-600 font-semibold">{currentPage}</span> of {totalPages}
            </span>
            <div className="flex items-center gap-1.5">
              <motion.button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium transition-colors ${currentPage === 1 ? "border-slate-100 text-slate-300 cursor-not-allowed" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                whileTap={{ scale: 0.92 }}
              >‹</motion.button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => totalPages <= 5 || page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) acc.push('...');
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-slate-300 text-xs">…</span>
                  ) : (
                    <motion.button
                      key={item}
                      onClick={() => setCurrentPage(item)}
                      className={`w-8 h-8 flex items-center justify-center rounded-xl border text-[12px] font-semibold transition-colors ${currentPage === item ? "bg-slate-800 border-slate-800 text-white shadow-sm" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                      whileTap={{ scale: 0.92 }}
                    >{item}</motion.button>
                  )
                )}

              <motion.button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium transition-colors ${currentPage === totalPages ? "border-slate-100 text-slate-300 cursor-not-allowed" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                whileTap={{ scale: 0.92 }}
              >›</motion.button>
            </div>
          </div>
        )}
      </div>

      {/* View Modal — unchanged */}
      {showViewModal && viewCompOff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowViewModal(false)}>
          <motion.div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Comp Off Details</h3>
              <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Comp Off Date</p>
                <div className="flex justify-between items-center">
                  <p className="font-medium text-slate-800">{viewCompOff.onDate}</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${viewCompOff.comOffStatus === "APPROVED" ? "bg-green-100 text-green-800" : viewCompOff.comOffStatus === "PENDING" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>{viewCompOff.comOffStatus}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500">Reason</p>
                <p className="font-medium text-slate-800">{viewCompOff.reason}</p>
              </div>
              {viewCompOff.comOffStatus === "REJECTED" && viewCompOff.approverComment && (
                <div>
                  <p className="text-sm text-slate-500">Rejection Reason</p>
                  <p className="font-medium text-slate-800">{viewCompOff.approverComment}</p>
                </div>
              )}
              <div className="flex justify-center pt-4">
                <button onClick={() => setShowViewModal(false)} className="w-full px-4 py-2 text-sm font-medium text-slate-50 hover:text-slate-800 rounded-full bg-slate-600 hover:bg-slate-400">Close</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Apply Form Modal — unchanged */}
      {showApplyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50" onClick={(e) => { if (!isSubmitting && e.target === e.currentTarget) { setShowApplyForm(false); setIsEditing(false); setNewCompOff({ onDate: '', reason: '', duration: TIME_CONFIG.FULL_DAY.duration, timing: 'FULL_DAY' }); } }}>
          <motion.div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">{isEditing ? 'Edit Comp Off' : 'Add Comp Off'}</h3>
              <button onClick={() => { if (!isSubmitting) { setShowApplyForm(false); setIsEditing(false); setNewCompOff({ onDate: '', reason: '', duration: TIME_CONFIG.FULL_DAY.duration, timing: 'FULL_DAY' }); } }} disabled={isSubmitting} className={`text-slate-400 hover:text-slate-600 ${isSubmitting ? 'cursor-not-allowed opacity-50' : ''}`}>✕</button>
            </div>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Comp Off Date</label>
                  <DatePicker selected={newCompOff.onDate}  onChange={(date) => setNewCompOff({ ...newCompOff, onDate: date })} dateFormat="dd-MM-yyyy" placeholderText="📅 Select date" filterDate={(date) => !isWeekdayAndNotHoliday(date, holidays) && isWithinPreviousFiveDays(date)} required disabled={isEditing} className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500" 
                    dayClassName={(date) => {
    const allowed =
      !isWeekdayAndNotHoliday(date, holidays) && isWithinPreviousFiveDays(date);

    return allowed
      ? ""
      : "text-slate-300 bg-slate-100 cursor-not-allowed rounded-full";
  }}
                    />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Timing</label>
                  <select value={newCompOff.timing} onChange={(e) => { const selectedKey = e.target.value; const config = TIME_CONFIG[selectedKey]; setNewCompOff({ ...newCompOff, duration: config.duration, timing: selectedKey }); }} className="w-full border border-slate-200 rounded-lg p-3" required>
                    {Object.entries(TIME_CONFIG).map(([key, config]) => (<option key={key} value={key}>{config.label}</option>))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                <textarea value={newCompOff.reason} onChange={(e) => setNewCompOff({ ...newCompOff, reason: e.target.value })} className="w-full border border-slate-200 rounded-lg p-2.5" rows="3" required></textarea>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowApplyForm(false); setIsEditing(false); setNewCompOff({ onDate: '', reason: '', duration: TIME_CONFIG.FULL_DAY.duration, timing: 'FULL_DAY' }); }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium bg-slate-800 text-white rounded-full hover:bg-slate-700 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]">
                  {isSubmitting ? (<div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>{isEditing ? 'Updating...' : 'Submitting...'}</span></div>) : (<span>{isEditing ? 'Update' : 'Submit'}</span>)}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default E_CompOff;